import React from "react";
import { NavLink } from "react-router-dom";

import { useCalendarContext } from "../../../lib/context/Calendar/CalendarContext";

import { CalendarEmail } from "./CalendarEmail/CalendarEmail";
import { CalendarProvider } from "./CalendarProvider/CalendarProvider";
import { CrossOrgCalendarSection } from "./CrossOrgCalendarSection/CrossOrgCalendarSection";
import { RoomFormik } from "../../shared/Forms/RoomForm/lib/useRoomFormik";
import { HandleLoadingState } from "../../shared/HandleLoadingState/HandleLoadingState";
import { LabelDirectionType } from "../../shared/LabelWithInformationBox/LabelWithInformationBox";
import { CALENDARS_ROOT_PATH } from "../../../lib/routes/routes";
import { CalendarType } from "../../../api/grpc/calendarprovider/calendarprovider";
import { MicrosoftRoomCalendarNotice } from "../MicrosoftRoomCalendarNotice/MicrosoftRoomCalendarNotice";

interface Props {
  formik: RoomFormik;
  direction?: LabelDirectionType;
  roomId?: string;
  edit?: boolean;
  hasCrossOrgCalendarEntitlement?: boolean;
  canShowCrossOrgSection?: boolean;
  canEditCrossOrgSection?: boolean;
  canAddSecondCrossOrg?: boolean;
}

export const CalendarProviderSelect = (props: Props) => {
  const {
    formik,
    direction,
    roomId,
    edit,
    hasCrossOrgCalendarEntitlement = false,
    canShowCrossOrgSection,
    canEditCrossOrgSection,
    canAddSecondCrossOrg,
  } = props;
  const { loading, providers } = useCalendarContext();
  const activeProvider = providers.find(
    (provider) => provider.iD === formik.values.calendarProviderId
  );
  const isMicrosoft365Provider =
    activeProvider?.calType === CalendarType.O365;

  const handleMainCalendarRemove = async () => {
    // When removing main calendar, also remove all cross-org calendars
    await formik.setFieldValue("calendarProviderId", "", true);
    await formik.setFieldValue("calendarId", "", true);
    await formik.setFieldValue("resourceEmail", "", true);
    await formik.setFieldValue("isManualCalendar", false, true);
    await formik.setFieldValue("calendarType", "", true);
    await formik.setFieldValue("crossOrganizationIntegration", [], true);
  };

  return (
    <HandleLoadingState
      loading={loading}
      loadingPlaceholder={<p>Loading calendars...</p>}
    >
      {providers.length > 0 ? (
        <>
          <CalendarProvider
            providers={providers}
            value={formik.values.calendarProviderId}
            error={formik.errors.calendarProviderId}
            direction={direction}
            label="Select calendar integration"
            onChange={async (value, type) => {
              await formik.setFieldValue("calendarProviderId", value, true);

              await formik.setFieldValue("calendarId", "", true);
              await formik.setFieldValue("resourceEmail", "", true);
              await formik.setFieldValue("isManualCalendar", false, true);
              await formik.setFieldValue("calendarType", type, true);
            }}
            onRemove={handleMainCalendarRemove}
          />
          {isMicrosoft365Provider && <MicrosoftRoomCalendarNotice />}
          {formik.values.calendarProviderId !== "" && (
            <CalendarEmail
              formik={formik}
              providers={providers}
              roomId={roomId}
              label="Select room calendar"
            />
          )}
          <CrossOrgCalendarSection
            formik={formik}
            providers={providers}
            direction={direction}
            roomId={roomId}
            hasCrossOrgCalendarEntitlement={hasCrossOrgCalendarEntitlement}
            edit={edit}
            canShowCrossOrgSection={canShowCrossOrgSection}
            canEditCrossOrgSection={canEditCrossOrgSection}
            canAddSecondCrossOrg={canAddSecondCrossOrg}
          />
        </>
      ) : (
        <NavLink
          to={CALENDARS_ROOT_PATH}
          className="btn btn-outline btn-small mt-2"
        >
          Add Calendar Integration
        </NavLink>
      )}
    </HandleLoadingState>
  );
};
